import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router';
import { Game } from '../types/curling';
import { getGame } from '../utils/storage';
import { ArrowLeft, Gauge } from 'lucide-react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceArea, ReferenceLine } from 'recharts';
import { fitSmoothCurve } from '../utils/curveFit';
import {
  DeviationStats,
  SheetSubScore,
  computeSheetScoring,
  sampleWithinKnownRange,
} from '../utils/sheetScoring';

// Broom-position axis (feet from center): the real -6 to +6 range
// broomPosition is stored in, plus a bookend tick on each side for the edge
// of the sheet — matches SheetMap's Curl by Path chart.
const POSITION_TICKS = [-7, -6, -5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5, 6, 7];

// Fixed y-axis range (rather than scaled to the data) so a chart's peaks read
// against a constant, comparable scale across games instead of each one
// auto-zooming to its own worst deviation.
const CURL_CHART_RANGE_FT = 3;

// A gridline every half foot across the fixed range above. Needs the y-axis's
// interval prop set to 0 to actually take effect — recharts otherwise thins
// tick labels to whatever fits the chart's pixel height (its default
// collision-avoidance interval), silently dropping most of an explicit list.
const CURL_CHART_TICKS = Array.from(
  { length: (CURL_CHART_RANGE_FT * 2) / 0.5 + 1 },
  (_, i) => Number((-CURL_CHART_RANGE_FT + i * 0.5).toFixed(2)),
);

// Same idea as the curl range/ticks above, sized for seconds instead of feet.
const WEIGHT_CHART_RANGE_S = 1.5;
const WEIGHT_CHART_TICKS = Array.from(
  { length: (WEIGHT_CHART_RANGE_S * 2) / 0.25 + 1 },
  (_, i) => Number((-WEIGHT_CHART_RANGE_S + i * 0.25).toFixed(2)),
);

const positionTickLabel = (v: number): string => {
  if (v <= -7) return 'Left Edge';
  if (v >= 7) return 'Right Edge';
  if (v === 0) return 'CL';
  return `${v > 0 ? '+' : ''}${v}ft`;
};

export function BRCScoringSheet() {
  const navigate = useNavigate();
  const location = useLocation();
  const { gameId } = useParams<{ gameId: string }>();
  const [game, setGame] = useState<Game | null>(null);

  useEffect(() => {
    if (!gameId) return;
    let cancelled = false;

    getGame(gameId)
      .then(foundGame => {
        if (!cancelled && foundGame) setGame(foundGame);
      })
      .catch(error => console.error('Failed to load BRC Scoring data:', error));

    return () => {
      cancelled = true;
    };
  }, [gameId]);

  const renderFlattenedCurlCard = (stats: DeviationStats | null, endLabel: string) => {
    if (!stats) return null;
    const deviationCurve = fitSmoothCurve(stats.deviation);
    if (!deviationCurve) return null;

    return (
      <>
        <h3 className="text-lg font-semibold text-white mb-4">{endLabel} End — Sheet Info</h3>
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white/8 border border-white/15 rounded-xl p-4">
            <p className="text-sm text-blue-300 mb-1">Max Variance</p>
            <p className="text-2xl font-bold text-white">{(stats.average + stats.maxVariance).toFixed(2)} ft</p>
          </div>
          <div className="bg-white/8 border border-white/15 rounded-xl p-4">
            <p className="text-sm text-blue-300 mb-1">Min Variance</p>
            <p className="text-2xl font-bold text-white">{(stats.average + stats.minVariance).toFixed(2)} ft</p>
          </div>
          <div className="bg-white/8 border border-white/15 rounded-xl p-4">
            <p className="text-sm text-blue-300 mb-1">Average Curl</p>
            <p className="text-2xl font-bold text-white">{stats.average.toFixed(2)} ft</p>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-white mb-1">Sheet Shape - Normalized Curl Pattern</h3>
        <p className="text-xs text-slate-400 mb-4">
          Calculated curl variance from the sheet's expected curl-by-position pattern.
        </p>
        <ResponsiveContainer width="100%" height={320}>
          <ScatterChart margin={{ top: 30, right: 30, bottom: 40, left: 60 }}>
            <CartesianGrid strokeDasharray="3 3" syncWithTicks />
            {/* House-ring shading for orientation, same bands as SheetMap. */}
            <ReferenceArea x1={-6} x2={-4} fill="#2563eb" fillOpacity={0.25} ifOverflow="visible" />
            <ReferenceArea x1={4} x2={6} fill="#2563eb" fillOpacity={0.25} ifOverflow="visible" />
            <ReferenceArea x1={-4} x2={-2} fill="#e2e8f0" fillOpacity={0.14} ifOverflow="visible" />
            <ReferenceArea x1={2} x2={4} fill="#e2e8f0" fillOpacity={0.14} ifOverflow="visible" />
            <ReferenceArea x1={-2} x2={-0.5} fill="#dc2626" fillOpacity={0.3} ifOverflow="visible" />
            <ReferenceArea x1={0.5} x2={2} fill="#dc2626" fillOpacity={0.3} ifOverflow="visible" />
            <ReferenceArea x1={-0.5} x2={0.5} fill="#ffffff" fillOpacity={0.55} ifOverflow="visible" />
            <ReferenceLine y={0} stroke="#fbbf24" strokeWidth={2} />
            <XAxis
              dataKey="position"
              name="Position"
              type="number"
              domain={[-7, 7]}
              ticks={POSITION_TICKS}
              tickFormatter={positionTickLabel}
              tick={{ fill: '#ffffff' }}
              label={{ value: 'Broom Position', position: 'insideBottom', offset: -10, fill: '#ffffff' }}
            />
            <YAxis
              dataKey="curl"
              name="Variance"
              unit=" ft"
              domain={[-CURL_CHART_RANGE_FT, CURL_CHART_RANGE_FT]}
              ticks={CURL_CHART_TICKS}
              interval={0}
              tickFormatter={(v: number) => v.toFixed(2)}
              tick={{ fill: '#ffffff' }}
              label={{ value: 'Curl Variance (ft)', angle: -90, position: 'insideLeft', offset: -10, dy: 70, fill: '#ffffff' }}
            />
            <Tooltip
              cursor={{ strokeDasharray: '3 3' }}
              content={({ payload }) => {
                if (payload && payload.length > 0) {
                  const p = payload[0].payload;
                  return (
                    <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-lg">
                      <p className="text-sm text-gray-600">Position: {p.position.toFixed(1)} ft</p>
                      <p className="text-sm text-gray-600">Variance: {p.curl >= 0 ? '+' : ''}{p.curl.toFixed(2)} ft</p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Scatter
              name="Flattened Trend"
              data={Array.from({ length: 121 }, (_, i) => {
                const position = -6 + (i / 120) * 12;
                return { position, curl: deviationCurve(position) };
              })}
              fill="#ef4444"
              line={{ stroke: '#ef4444', strokeWidth: 3 }}
              lineType="joint"
              shape={(props: any) => <circle cx={props.cx} cy={props.cy} r={0} fill="none" />}
              isAnimationActive={false}
            />
          </ScatterChart>
        </ResponsiveContainer>
      </>
    );
  };

  const renderWeightByPositionCard = (stats: DeviationStats | null) => {
    if (!stats) return null;
    const deviationCurve = fitSmoothCurve(stats.deviation);
    if (!deviationCurve) return null;
    const trend = sampleWithinKnownRange(stats.deviation, deviationCurve, 121);

    return (
      <div className="mt-8 pt-6 border-t border-white/10">
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white/8 border border-white/15 rounded-xl p-4">
            <p className="text-sm text-blue-300 mb-1">Max Weight Variance</p>
            <p className="text-2xl font-bold text-white">{(stats.average + stats.maxVariance).toFixed(2)} s</p>
          </div>
          <div className="bg-white/8 border border-white/15 rounded-xl p-4">
            <p className="text-sm text-blue-300 mb-1">Min Weight Variance</p>
            <p className="text-2xl font-bold text-white">{(stats.average + stats.minVariance).toFixed(2)} s</p>
          </div>
          <div className="bg-white/8 border border-white/15 rounded-xl p-4">
            <p className="text-sm text-blue-300 mb-1">Average Draw Weight</p>
            <p className="text-2xl font-bold text-white">{stats.average.toFixed(2)} s</p>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-white mb-1">Weight by Broom Position</h3>
        <p className="text-xs text-slate-400 mb-4">
          Calculated tee line weight with first draw down a path being ignored for break in.
        </p>
        <ResponsiveContainer width="100%" height={320}>
          <ScatterChart margin={{ top: 30, right: 30, bottom: 40, left: 60 }}>
            <CartesianGrid strokeDasharray="3 3" syncWithTicks />
            <ReferenceArea x1={-6} x2={-4} fill="#2563eb" fillOpacity={0.25} ifOverflow="visible" />
            <ReferenceArea x1={4} x2={6} fill="#2563eb" fillOpacity={0.25} ifOverflow="visible" />
            <ReferenceArea x1={-4} x2={-2} fill="#e2e8f0" fillOpacity={0.14} ifOverflow="visible" />
            <ReferenceArea x1={2} x2={4} fill="#e2e8f0" fillOpacity={0.14} ifOverflow="visible" />
            <ReferenceArea x1={-2} x2={-0.5} fill="#dc2626" fillOpacity={0.3} ifOverflow="visible" />
            <ReferenceArea x1={0.5} x2={2} fill="#dc2626" fillOpacity={0.3} ifOverflow="visible" />
            <ReferenceArea x1={-0.5} x2={0.5} fill="#ffffff" fillOpacity={0.55} ifOverflow="visible" />
            <ReferenceLine y={0} stroke="#fbbf24" strokeWidth={2} />
            <XAxis
              dataKey="x"
              name="Position"
              type="number"
              domain={[-7, 7]}
              ticks={POSITION_TICKS}
              tickFormatter={positionTickLabel}
              tick={{ fill: '#ffffff' }}
              label={{ value: 'Broom Position', position: 'insideBottom', offset: -10, fill: '#ffffff' }}
            />
            <YAxis
              dataKey="y"
              name="Variance"
              unit=" s"
              domain={[-WEIGHT_CHART_RANGE_S, WEIGHT_CHART_RANGE_S]}
              ticks={WEIGHT_CHART_TICKS}
              interval={0}
              tickFormatter={(v: number) => v.toFixed(2)}
              tick={{ fill: '#ffffff' }}
              label={{ value: 'Weight Variance (s)', angle: -90, position: 'insideLeft', offset: -10, dy: 70, fill: '#ffffff' }}
            />
            <Tooltip
              cursor={{ strokeDasharray: '3 3' }}
              content={({ payload }) => {
                if (payload && payload.length > 0) {
                  const p = payload[0].payload;
                  return (
                    <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-lg">
                      <p className="text-sm text-gray-600">Position: {p.x.toFixed(1)} ft</p>
                      <p className="text-sm text-gray-600">Variance: {p.y >= 0 ? '+' : ''}{p.y.toFixed(2)} s</p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Scatter
              name="Weight Trend"
              data={trend}
              fill="#22d3ee"
              line={{ stroke: '#22d3ee', strokeWidth: 3 }}
              lineType="joint"
              shape={(props: any) => <circle cx={props.cx} cy={props.cy} r={0} fill="none" />}
              isAnimationActive={false}
            />
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const renderCurlByEndCard = (stats: DeviationStats | null) => {
    if (!stats) return null;
    const deviationCurve = fitSmoothCurve(stats.deviation);
    if (!deviationCurve) return null;
    const trend = sampleWithinKnownRange(stats.deviation, deviationCurve, 121);

    const minEnd = stats.raw[0].x;
    const maxEnd = stats.raw[stats.raw.length - 1].x;
    const endTicks = Array.from({ length: maxEnd - minEnd + 1 }, (_, i) => minEnd + i);

    return (
      <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6 mt-6">
        <h3 className="text-lg font-semibold text-white mb-4">Generalized Curl by End</h3>
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white/8 border border-white/15 rounded-xl p-4">
            <p className="text-sm text-blue-300 mb-1">Max Variance</p>
            <p className="text-2xl font-bold text-white">{(stats.average + stats.maxVariance).toFixed(2)} ft</p>
          </div>
          <div className="bg-white/8 border border-white/15 rounded-xl p-4">
            <p className="text-sm text-blue-300 mb-1">Min Variance</p>
            <p className="text-2xl font-bold text-white">{(stats.average + stats.minVariance).toFixed(2)} ft</p>
          </div>
          <div className="bg-white/8 border border-white/15 rounded-xl p-4">
            <p className="text-sm text-blue-300 mb-1">Average Curl</p>
            <p className="text-2xl font-bold text-white">{stats.average.toFixed(2)} ft</p>
          </div>
        </div>

        <p className="text-xs text-slate-400 mb-4">
          Calculated curl variance per end, normalized so the game average sits at zero.
        </p>
        <ResponsiveContainer width="100%" height={320}>
          <ScatterChart margin={{ top: 30, right: 30, bottom: 40, left: 60 }}>
            <CartesianGrid strokeDasharray="3 3" syncWithTicks />
            <ReferenceLine y={0} stroke="#fbbf24" strokeWidth={2} />
            <XAxis
              dataKey="x"
              name="End"
              type="number"
              domain={[minEnd, maxEnd]}
              ticks={endTicks}
              allowDecimals={false}
              tick={{ fill: '#ffffff' }}
              label={{ value: 'End', position: 'insideBottom', offset: -10, fill: '#ffffff' }}
            />
            <YAxis
              dataKey="y"
              name="Variance"
              unit=" ft"
              domain={[-CURL_CHART_RANGE_FT, CURL_CHART_RANGE_FT]}
              ticks={CURL_CHART_TICKS}
              interval={0}
              tickFormatter={(v: number) => v.toFixed(2)}
              tick={{ fill: '#ffffff' }}
              label={{ value: 'Curl Variance (ft)', angle: -90, position: 'insideLeft', offset: -10, dy: 70, fill: '#ffffff' }}
            />
            <Tooltip
              cursor={{ strokeDasharray: '3 3' }}
              content={({ payload }) => {
                if (payload && payload.length > 0) {
                  const p = payload[0].payload;
                  return (
                    <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-lg">
                      <p className="text-sm text-gray-600">End: {p.x}</p>
                      <p className="text-sm text-gray-600">Variance: {p.y >= 0 ? '+' : ''}{p.y.toFixed(2)} ft</p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Scatter
              name="Curl by End"
              data={trend}
              fill="#ef4444"
              line={{ stroke: '#ef4444', strokeWidth: 3 }}
              lineType="joint"
              shape={(props: any) => <circle cx={props.cx} cy={props.cy} r={0} fill="none" />}
              isAnimationActive={false}
            />
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const renderWeightByEndCard = (stats: DeviationStats | null) => {
    if (!stats) return null;
    const deviationCurve = fitSmoothCurve(stats.deviation);
    if (!deviationCurve) return null;
    const trend = sampleWithinKnownRange(stats.deviation, deviationCurve, 121);

    const minEnd = stats.raw[0].x;
    const maxEnd = stats.raw[stats.raw.length - 1].x;
    const endTicks = Array.from({ length: maxEnd - minEnd + 1 }, (_, i) => minEnd + i);

    return (
      <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6 mt-6">
        <h3 className="text-lg font-semibold text-white mb-4">Generalized Weight by End</h3>
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white/8 border border-white/15 rounded-xl p-4">
            <p className="text-sm text-blue-300 mb-1">Max Weight Variance</p>
            <p className="text-2xl font-bold text-white">{(stats.average + stats.maxVariance).toFixed(2)} s</p>
          </div>
          <div className="bg-white/8 border border-white/15 rounded-xl p-4">
            <p className="text-sm text-blue-300 mb-1">Min Weight Variance</p>
            <p className="text-2xl font-bold text-white">{(stats.average + stats.minVariance).toFixed(2)} s</p>
          </div>
          <div className="bg-white/8 border border-white/15 rounded-xl p-4">
            <p className="text-sm text-blue-300 mb-1">Average Draw Weight</p>
            <p className="text-2xl font-bold text-white">{stats.average.toFixed(2)} s</p>
          </div>
        </div>

        <p className="text-xs text-slate-400 mb-4">
          Calculated tee-line-normalized draw weight per end, first rock down each path ignored for break in.
        </p>
        <ResponsiveContainer width="100%" height={320}>
          <ScatterChart margin={{ top: 30, right: 30, bottom: 40, left: 60 }}>
            <CartesianGrid strokeDasharray="3 3" syncWithTicks />
            <ReferenceLine y={0} stroke="#fbbf24" strokeWidth={2} />
            <XAxis
              dataKey="x"
              name="End"
              type="number"
              domain={[minEnd, maxEnd]}
              ticks={endTicks}
              allowDecimals={false}
              tick={{ fill: '#ffffff' }}
              label={{ value: 'End', position: 'insideBottom', offset: -10, fill: '#ffffff' }}
            />
            <YAxis
              dataKey="y"
              name="Variance"
              unit=" s"
              domain={[-WEIGHT_CHART_RANGE_S, WEIGHT_CHART_RANGE_S]}
              ticks={WEIGHT_CHART_TICKS}
              interval={0}
              tickFormatter={(v: number) => v.toFixed(2)}
              tick={{ fill: '#ffffff' }}
              label={{ value: 'Weight Variance (s)', angle: -90, position: 'insideLeft', offset: -10, dy: 70, fill: '#ffffff' }}
            />
            <Tooltip
              cursor={{ strokeDasharray: '3 3' }}
              content={({ payload }) => {
                if (payload && payload.length > 0) {
                  const p = payload[0].payload;
                  return (
                    <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-lg">
                      <p className="text-sm text-gray-600">End: {p.x}</p>
                      <p className="text-sm text-gray-600">Variance: {p.y >= 0 ? '+' : ''}{p.y.toFixed(2)} s</p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Scatter
              name="Weight by End"
              data={trend}
              fill="#22d3ee"
              line={{ stroke: '#22d3ee', strokeWidth: 3 }}
              lineType="joint"
              shape={(props: any) => <circle cx={props.cx} cy={props.cy} r={0} fill="none" />}
              isAnimationActive={false}
            />
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    );
  };

  if (!game) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900 p-6 flex items-center justify-center">
        <p className="text-blue-300">Loading…</p>
      </div>
    );
  }

  const scoring = computeSheetScoring(game);

  const renderSubScoreCard = (s: SheetSubScore) => (
    <div key={s.label} className="bg-white/8 border border-white/15 rounded-xl p-4">
      <p className="text-sm text-blue-300 mb-1">{s.label}</p>
      <p className={`text-2xl font-bold ${
        s.value === null ? 'text-slate-500'
          : s.value >= 8 ? 'text-emerald-400'
          : s.value >= 5 ? 'text-amber-400'
          : 'text-red-400'
      }`}>
        {s.value === null ? '—' : `${s.value.toFixed(1)} / 10`}
      </p>
      <p className="text-xs text-slate-400 mt-1">{s.description}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6 mb-6">
          <button
            onClick={() => {
              if (location.key !== 'default') {
                navigate(-1);
              } else {
                navigate(`/CurlingCodex/game/${gameId}/brc-scoring`);
              }
            }}
            className="flex items-center gap-2 text-blue-300 hover:text-blue-200 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          <div className="flex items-center gap-3 mb-2">
            <Gauge className="w-6 h-6 text-blue-300" />
            <h1 className="text-2xl font-bold text-white">BRC Scoring - Sheet</h1>
          </div>
          <p className="text-sm text-blue-300">
            {game.curlingClub}{game.curlingClub && game.sheetNumber && ' • '}{game.sheetNumber && `Sheet ${game.sheetNumber}`}
          </p>
          <p className="text-xs text-slate-400 mt-2 max-w-3xl">
            A flattened version of the curl and weight trend lines, with the sheet's expected
            by-position pattern subtracted out so only genuine anomalies stand out.
          </p>
        </div>

        {!scoring.hasData ? (
          <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-12 text-center">
            <Gauge className="w-16 h-16 text-blue-300/70 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Shots Recorded Yet</h3>
            <p className="text-blue-300">Scoring needs at least a few recorded shots to work with.</p>
          </div>
        ) : (
          <>
            <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6 mt-6">
              <h3 className="text-lg font-semibold text-white mb-1">Sheet Score</h3>
              <p className="text-xs text-slate-400 mb-4">
                {scoring.determinedSubScoreCount} of {scoring.subScores.length} sub-scores determined so far.
              </p>
              <div className="flex items-baseline gap-2 mb-6">
                <span className="text-5xl font-bold text-white">{scoring.overallScore.toFixed(0)}%</span>
              </div>
              <div className="flex items-baseline gap-2 mb-3">
                <p className="text-sm font-semibold text-blue-300">Curl</p>
                <span className="text-lg font-bold text-white">
                  {scoring.compositeCurlScore !== null ? scoring.compositeCurlScore.toFixed(1) : '—'}
                </span>
                <span className="text-sm text-blue-300">/ 10</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                {scoring.curlSubScores.map(s => renderSubScoreCard(s))}
              </div>

              <div className="flex items-baseline gap-2 mb-3">
                <p className="text-sm font-semibold text-blue-300">Weight</p>
                <span className="text-lg font-bold text-white">
                  {scoring.compositeWeightScore !== null ? scoring.compositeWeightScore.toFixed(1) : '—'}
                </span>
                <span className="text-sm text-blue-300">/ 10</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {scoring.weightSubScores.map(s => renderSubScoreCard(s))}
              </div>
            </div>

            <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6 mt-6">
              {renderFlattenedCurlCard(scoring.awayCurlStats, 'Away')}
              {renderWeightByPositionCard(scoring.awayWeightStats)}
            </div>

            <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6 mt-6">
              {renderFlattenedCurlCard(scoring.homeCurlStats, 'Home')}
              {renderWeightByPositionCard(scoring.homeWeightStats)}
            </div>

            {renderCurlByEndCard(scoring.curlByEndStats)}
            {renderWeightByEndCard(scoring.weightByEndStats)}

            <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6 mt-6">
              <h3 className="text-lg font-semibold text-white mb-4">Appendix</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-semibold text-blue-300 mb-1">Sheet Shape - Normalized Curl Pattern</p>
                  <p className="text-xs text-slate-400">
                    Curl at each foot of broom position, with the expected sheet-wide bowl shape subtracted
                    out — a flat line means the ice is behaving as expected; peaks and valleys mark where it
                    isn't.
                  </p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-blue-300 mb-1">Weight by Broom Position</p>
                  <p className="text-xs text-slate-400">
                    Every draw shot (zones 4-10) is first re-based onto a tee-line-equivalent time — its own
                    time minus its own called weight, plus this game's average tee-line weight — since the
                    different draw zones are genuinely called at different weights and averaging their raw
                    times together would just measure which zone got called more. From there: draw-weight
                    time at each foot of broom position, with the expected sheet-wide pace subtracted out
                    (first rock down each path excluded as a warm-up throw) — a flat line means pace is
                    consistent across the sheet. Edges with no recorded draws are left blank rather than
                    guessed at.
                  </p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-blue-300 mb-1">Generalized Weight by End</p>
                  <p className="text-xs text-slate-400">
                    Same tee-line normalization as Weight by Broom Position, averaged per end instead of per
                    position: draw-weight time per end, across the whole game, with the expected game-long
                    pace subtracted out (first rock down each path excluded) — a flat line means pace held
                    steady; peaks and valleys mark where an end ran unusually fast or slow.
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
